import { computed, Injectable, inject, resource } from '@angular/core';
import { Session } from '../session/session';
import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import { OrgId } from '../shared/ids';
import { loadOnceResource } from '../shared/load-once-resource';
import { resourceSettler } from '../shared/resource-settled';
import {
  brandsUnlockedBy,
  isCategorySubscribed,
  isEffectivelySubscribed,
  quotaRemaining,
  skuHasSalesData,
  subscribedCategorySlugs,
  usagePercent,
} from './coverage';
import { brandAccessPolicyFor, type Entitlement, emptyEntitlement } from './entitlement';
import {
  type BrandCategorySkuCounts,
  EntitlementRepository,
  type EntitlementSnapshot,
} from './entitlement-repository';

const EMPTY_BRAND_BY_EAN: ReadonlyMap<EAN, BrandSlug> = new Map();
const NO_COUNTS: BrandCategorySkuCounts = {};
/** Placeholder identity for the empty entitlement shown while no org is current. */
const NO_ORG = OrgId('unknown');

/**
 * Entitlement state (brand allow lists and Coverage) for the current organization.
 *
 * The org id is the resource's parameter, so the snapshot is keyed by org by
 * construction: a switch discards the previous org's entitlement and loads the
 * new one; a response for a superseded org never lands. The row is read once
 * per org: `portal_org` is not in the realtime publication.
 */
@Injectable({ providedIn: 'root' })
export class EntitlementStore {
  private readonly repo = inject(EntitlementRepository);
  private readonly session = inject(Session);

  private readonly currentOrgId = computed(() => this.session.currentOrgId() ?? undefined);

  private readonly snapshot = resource<EntitlementSnapshot, OrgId | undefined>({
    params: () => this.currentOrgId(),
    loader: ({ params }) => this.repo.forOrg(params),
  });

  // Global reference data, fetched on first request and kept for the session.
  private readonly counts = loadOnceResource(() => this.repo.loadBrandCategorySkuCounts());

  readonly settled = resourceSettler(this.snapshot, { after: () => this.session.settled() });

  readonly loaded = computed(() => this.snapshot.hasValue());
  /** Why the entitlement or the Session's member orgs failed to load, null while they load or hold. */
  readonly error = computed<Error | null>(
    () => this.snapshot.error() ?? this.session.error() ?? null
  );

  readonly entitlement = computed<Entitlement>(() =>
    this.snapshot.hasValue()
      ? this.snapshot.value().entitlement
      : emptyEntitlement(this.currentOrgId() ?? NO_ORG)
  );

  readonly coverage = computed(() => this.entitlement().coverage);

  readonly brandAccess = computed(() => brandAccessPolicyFor(this.entitlement()));

  /** Catalog EAN→brand facts riding on the org row; covers the category-caused EANs. */
  readonly catalogBrandByEan = computed<ReadonlyMap<EAN, BrandSlug>>(() =>
    this.snapshot.hasValue() ? this.snapshot.value().catalogBrandByEan : EMPTY_BRAND_BY_EAN
  );

  readonly brandCategorySkuCounts = computed<BrandCategorySkuCounts>(
    () => this.counts.value() ?? NO_COUNTS
  );

  readonly quota = computed(() => this.coverage().quota);
  readonly totalSkus = computed(() => this.coverage().quota.total);
  readonly activeSkus = computed(() => this.coverage().quota.active);
  readonly maxSkus = computed(() => this.coverage().quota.max);
  readonly remaining = computed(() => quotaRemaining(this.coverage()));
  readonly usagePercent = computed(() => usagePercent(this.coverage()));

  readonly subscribedSkus = computed(() => this.coverage().subscribedSkus);
  readonly categorySubscriptions = computed(() => this.coverage().categorySubscriptions);

  readonly subscribedCategorySlugs = computed(() => subscribedCategorySlugs(this.coverage()));

  /**
   * Ensures the brand/category SKU counts are loaded: a no-op once they are,
   * and a fresh read after a failed load.
   */
  loadBrandCategorySkuCounts(): Promise<void> {
    return this.counts.load();
  }

  isCategorySubscribed(slug: string): boolean {
    return isCategorySubscribed(this.coverage(), slug);
  }

  isEffectivelySubscribed(slug: string): boolean {
    return isEffectivelySubscribed(this.coverage(), slug);
  }

  brandsUnlockedBy(category: string): readonly BrandSlug[] {
    return brandsUnlockedBy(this.coverage(), category);
  }

  skuHasSalesData(ean: EAN): boolean {
    return skuHasSalesData(this.coverage(), ean);
  }
}
